import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from "react";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Wallet, UploadSimple, Trash, FileXls, Storefront, FloppyDisk, PencilSimple, ArrowsClockwise, CheckCircle, Warning, ArrowUUpLeft, DownloadSimple, CaretUp, CaretDown, Calendar, PushPin, PushPinSlash, X } from "@phosphor-icons/react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Hook: mirror a top scrollbar onto a bottom scroll container.
 * Returns { topRef, bodyRef } — keep both refs scroll-synced horizontally.
 */
function useDualHorizontalScroll() {
  const topRef = useRef(null);
  const bodyRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);

  useLayoutEffect(() => {
    const update = () => {
      if (bodyRef.current) setScrollWidth(bodyRef.current.scrollWidth);
    };
    update();
    const ro = new ResizeObserver(update);
    if (bodyRef.current) ro.observe(bodyRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const t = topRef.current; const b = bodyRef.current;
    if (!t || !b) return;
    let syncing = false;
    const onTop = () => { if (syncing) return; syncing = true; b.scrollLeft = t.scrollLeft; syncing = false; };
    const onBot = () => { if (syncing) return; syncing = true; t.scrollLeft = b.scrollLeft; syncing = false; };
    t.addEventListener("scroll", onTop);
    b.addEventListener("scroll", onBot);
    return () => { t.removeEventListener("scroll", onTop); b.removeEventListener("scroll", onBot); };
  }, []);

  return { topRef, bodyRef, scrollWidth };
}

export default function OutstandingTab({ refreshKey, onPaymentChange }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalCurrent, setTotalCurrent] = useState(0);
  const [totalFresh, setTotalFresh] = useState(0);
  const [totalPlanned, setTotalPlanned] = useState(0);
  const [maxPoCount, setMaxPoCount] = useState(0);
  const [loading, setLoading] = useState(true);
  // Per-row local edit state: { [id]: { amount: string, approved: bool, dirty: bool } }
  const [edit, setEdit] = useState({});
  // Confirm-over-outstanding dialog state
  const [confirmRow, setConfirmRow] = useState(null); // { row, planned, approved }
  // Filters
  const [vendorQuery, setVendorQuery]   = useState("");
  const [savedFilter, setSavedFilter]   = useState("all");    // all | saved | not_saved
  const [approvedFilter, setApprovedFilter] = useState("all"); // all | approved | pending
  // Sort: vendor (default), total_outstanding, current_outstanding, last_purchase_date,
  // last_purchase_amount, planned_payment, payment_approved, fresh_outstanding
  const [sortBy, setSortBy] = useState("vendor");
  const [sortDir, setSortDir] = useState("asc");
  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      // Sensible defaults: text → asc; numbers/dates → desc (largest first)
      const textCols = new Set(["vendor", "product"]);
      setSortDir(textCols.has(key) ? "asc" : "desc");
    }
  };
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearing, setClearing] = useState(false);
  const fileInputRef = useRef(null);
  const { topRef, bodyRef, scrollWidth } = useDualHorizontalScroll();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/outstanding");
      setItems(r.data?.items || []);
      setTotal(r.data?.total_outstanding || 0);
      setTotalCurrent(r.data?.total_current_outstanding || 0);
      setTotalFresh(r.data?.total_fresh_outstanding || 0);
      setTotalPlanned(r.data?.total_planned_payment || 0);
      setMaxPoCount(r.data?.max_po_count || 0);
    } catch {
      toast.error("Failed to load outstanding");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  // ---- Payment editing helpers ----------------------------------------------
  const isEditing = (id) => edit[id] !== undefined;
  const startEdit = (row) => setEdit((e) => ({
    ...e,
    [row.id]: {
      amount: String(row.planned_payment ?? 0),
      approved: !!row.payment_approved,
      dirty: false,
    },
  }));
  const cancelEdit = (id) => setEdit((e) => { const next = { ...e }; delete next[id]; return next; });
  const setEditField = (id, field, value) => setEdit((e) => ({
    ...e,
    [id]: { ...(e[id] || { amount: "0", approved: false }), [field]: value, dirty: true },
  }));
  const savePayment = async (row, opts = {}) => {
    const st = edit[row.id];
    if (!st) return;
    const planned = parseFloat(st.amount || "0") || 0;

    // Guard: if planned > current outstanding, ask the user to confirm
    const current = Number(row.current_outstanding) || 0;
    if (!opts.skipOverdrawCheck && planned > 0 && planned > current) {
      setConfirmRow({ row, planned, approved: !!st.approved });
      return;
    }

    try {
      await api.patch(`/outstanding/${row.id}/payment`, {
        planned_payment: planned,
        payment_approved: !!st.approved,
      });
      toast.success(planned > 0
        ? `Saved · ${row.vendor} · ${st.approved ? "approved" : "pending approval"} · ₹${planned.toLocaleString("en-IN")}`
        : `Saved · ${row.vendor}`);
      cancelEdit(row.id);
      await load();
      onPaymentChange && onPaymentChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  // Called when user clicks YES in the over-outstanding confirmation
  const proceedWithOverdrawnPayment = async () => {
    if (!confirmRow) return;
    const { row } = confirmRow;
    await savePayment(row, { skipOverdrawCheck: true });
    setConfirmRow(null);
  };

  const rollbackRowPayment = async (row) => {
    const wasApproved = !!row.payment_approved;
    const amt = Number(row.planned_payment || 0);
    if (!amt && !wasApproved) return;
    const msg = wasApproved
      ? `Rollback APPROVED payment of ${fmtINR(amt)} for ${row.vendor}?\n\nThis will:\n  • clear the planned payment\n  • un-approve the row\n  • restore Current Outstanding back to ${fmtINR(row.current_outstanding)}\n  • reduce Committed Outflow accordingly`
      : `Rollback planned payment of ${fmtINR(amt)} for ${row.vendor}?\n\nThis will clear the planned amount and remove it from Committed Outflow.`;
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/outstanding/${row.id}/payment`, {
        planned_payment: 0,
        payment_approved: false,
      });
      toast.success(`Rolled back · ${row.vendor}`);
      cancelEdit(row.id);
      await load();
      onPaymentChange && onPaymentChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed");
    }
  };

  const refreshOutflow = async () => {
    if (!window.confirm(
        "Refresh Outflow will:\n" +
        "  • Book ALL approved payments into Total Outstanding (the money has actually gone out)\n" +
        "  • Clear all unapproved planned payments without booking them\n" +
        "  • Reset Committed Outflow contribution to ₹0\n\n" +
        "Proceed?"
    )) return;
    try {
      const r = await api.post("/outstanding/refresh-outflow");
      const { rows_booked = 0, rows_cleared = 0, booked_total = 0 } = r.data || {};
      const parts = [];
      if (rows_booked)  parts.push(`booked ${fmtINR(booked_total)} across ${rows_booked} vendor${rows_booked === 1 ? "" : "s"}`);
      if (rows_cleared) parts.push(`cleared ${rows_cleared} planned`);
      toast.success(parts.length ? `Outflow refreshed · ${parts.join(" · ")}` : "Nothing to refresh");
      setEdit({});
      await load();
      onPaymentChange && onPaymentChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    setProgress({ done: 0, total: files.length });
    let okCount = 0;
    let failCount = 0;
    for (const f of files) {
      const form = new FormData();
      form.append("file", f);
      try {
        const r = await api.post("/uploads/outstanding", form, { headers: { "Content-Type": "multipart/form-data" } });
        okCount++;
        toast.success(`${r.data?.vendor || f.name} · ${fmtINR(r.data?.total_outstanding || 0)} outstanding`);
      } catch (e) {
        failCount++;
        toast.error(`${f.name}: ${e?.response?.data?.detail || "upload failed"}`);
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setBusy(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (okCount) load();
    if (okCount && !failCount) toast.success(`Imported ${okCount} vendor ledger${okCount > 1 ? "s" : ""}`);
  };

  const remove = async (id, vendor) => {
    if (!window.confirm(`Remove outstanding entry for ${vendor}?`)) return;
    try {
      await api.delete(`/outstanding/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // ---- Download helpers -----------------------------------------------------
  const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadVendorLedger = async (row) => {
    try {
      const res = await api.get(`/outstanding/${row.id}/download`, { responseType: "blob" });
      // Try to read filename from Content-Disposition
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m ? m[1] : `${row.vendor || "vendor"} - Ledger.xlsx`;
      triggerBlobDownload(res.data, fname);
      toast.success(`Downloaded · ${row.vendor}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  const downloadAllVendorsReport = async () => {
    try {
      const res = await api.get("/outstanding/export/all", { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m ? m[1] : `Vendors Pending List.xlsx`;
      triggerBlobDownload(res.data, fname);
      toast.success(`Downloaded · ${items.length} vendors`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report download failed");
    }
  };

  // ---- Report preview modal -------------------------------------------------
  const [reportOpen, setReportOpen]   = useState(false);
  const [reportData, setReportData]   = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const openReportModal = async () => {
    setReportOpen(true);
    setReportLoading(true);
    setReportData(null);
    try {
      const r = await api.get("/outstanding/report");
      setReportData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
      setReportOpen(false);
    } finally {
      setReportLoading(false);
    }
  };

  // Static columns BEFORE the dynamic PO triplets
  const STATIC_LEFT_COUNT = 5; // Vendor, Product, Total Outstanding, Last Purchase Date, Last Purchase Amount
  const poCount = Math.max(maxPoCount, 0);

  // Apply filters
  const vq = vendorQuery.trim().toLowerCase();
  const filteredItems = items.filter((r) => {
    if (vq && !(r.vendor || "").toLowerCase().includes(vq)) return false;
    const hasSaved = Number(r.planned_payment || 0) > 0;
    if (savedFilter === "saved"     && !hasSaved) return false;
    if (savedFilter === "not_saved" &&  hasSaved) return false;
    if (approvedFilter === "approved" && !r.payment_approved) return false;
    if (approvedFilter === "pending"  &&  r.payment_approved) return false;
    return true;
  });

  // Apply sort
  const getSortVal = (r, key) => {
    switch (key) {
      case "vendor":               return (r.vendor || "").toLowerCase();
      case "product":              return (r.product || "").toLowerCase();
      case "total_outstanding":    return Number(r.total_outstanding || 0);
      case "current_outstanding":  return Number(r.current_outstanding || 0);
      case "fresh_outstanding":    return Number(r.fresh_outstanding || 0);
      case "last_purchase_date":   return r.last_purchase_date || "";
      case "last_purchase_amount": return Number(r.last_purchase_amount || 0);
      case "planned_payment":      return Number(r.planned_payment || 0);
      case "payment_approved":     return r.payment_approved ? 1 : 0;
      default: return 0;
    }
  };
  filteredItems.sort((a, b) => {
    const va = getSortVal(a, sortBy);
    const vb = getSortVal(b, sortBy);
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  // Totals reflect the filtered set so the table footer adds up to what's visible
  const fTotal        = filteredItems.reduce((s, r) => s + Number(r.total_outstanding   || 0), 0);
  const fTotalCurrent = filteredItems.reduce((s, r) => s + Number(r.current_outstanding || 0), 0);
  const fTotalFresh   = filteredItems.reduce((s, r) => s + Number(r.fresh_outstanding   || 0), 0);
  const fTotalPlanned = filteredItems.reduce((s, r) => s + Number(r.planned_payment     || 0), 0);
  const filtersActive = !!vq || savedFilter !== "all" || approvedFilter !== "all";
  const clearFilters = () => { setVendorQuery(""); setSavedFilter("all"); setApprovedFilter("all"); };

  return (
    <div data-testid="outstanding-tab" className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-sm grid place-items-center bg-cyan-900/40 text-cyan-200">
              <Wallet size={15} weight="duotone" />
            </div>
            <h2 className="font-heading font-bold text-2xl tracking-tight">Vendor outstanding</h2>
          </div>
          <div className="text-[11px] text-slate-400 mt-1.5">
            Tally outstanding · plus PetPooja POs raised after each vendor's last Tally purchase. <span className="text-cyan-300 font-semibold">Current Outstanding = Tally outstanding + Σ recent POs.</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="outstanding-file-input"
            disabled={busy}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={openReportModal}
            disabled={busy || items.length === 0}
            className="h-9 rounded-sm border-cyan-700 text-cyan-200 hover:bg-cyan-900/30 disabled:opacity-40"
            data-testid="outstanding-report-btn"
            title="Open Vendors Pending List report (preview + download)"
          >
            <DownloadSimple size={14} weight="bold" className="mr-1.5" />
            Report
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={refreshOutflow}
            disabled={busy || totalPlanned === 0}
            className="h-9 rounded-sm border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-40"
            data-testid="outstanding-refresh-outflow-btn"
            title="Reset all planned payments to zero (clears Committed Outflow contribution)"
          >
            <ArrowsClockwise size={14} weight="bold" className="mr-1.5" />
            Refresh Outflow
          </Button>
          <Button
            size="sm"
            onClick={() => (items.length > 0 ? setShowClearDialog(true) : fileInputRef.current?.click())}
            disabled={busy}
            className="h-9 rounded-sm bg-cyan-600 hover:bg-cyan-500 text-cyan-50"
            data-testid="outstanding-upload-btn"
          >
            <UploadSimple size={14} weight="bold" className="mr-1.5" />
            {busy ? `Uploading ${progress.done}/${progress.total}…` : "Upload ledger(s)"}
          </Button>
        </div>
      </div>

      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="outstanding-clear-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Fresh upload — clear existing Tally ledger data?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400 leading-relaxed">
              You currently have <span className="font-semibold text-cyan-300">{items.length}</span> vendor ledger{items.length === 1 ? "" : "s"} with a Tally outstanding of <span className="font-mono text-cyan-300">{fmtINR(total)}</span>.
              <br /><br />
              Choose how to handle the incoming file(s):
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel
              className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white mt-0"
              data-testid="outstanding-clear-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowClearDialog(false);
                fileInputRef.current?.click();
              }}
              className="bg-slate-700 hover:bg-slate-600 text-slate-100"
              data-testid="outstanding-clear-keep-btn"
            >
              Add to existing data
            </AlertDialogAction>
            <AlertDialogAction
              disabled={clearing}
              onClick={async () => {
                setClearing(true);
                try {
                  const res = await api.delete("/outstanding/clear");
                  toast.success(`Cleared ${res.data?.deleted || 0} ledger row(s)`);
                  await load();
                  setShowClearDialog(false);
                  // Give React one tick to close the dialog before opening the file picker
                  setTimeout(() => fileInputRef.current?.click(), 100);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed to clear existing data");
                } finally {
                  setClearing(false);
                }
              }}
              className="bg-rose-600 hover:bg-rose-500 text-white"
              data-testid="outstanding-clear-purge-btn"
            >
              {clearing ? "Clearing…" : "Clear existing & upload fresh"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
        <div className="rounded-md border border-cyan-900/40 bg-slate-900/50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Vendors tracked</div>
          <div className="font-mono tabular-nums text-2xl font-bold text-cyan-100 mt-1">{items.length}</div>
        </div>
        <div className="rounded-md border border-cyan-900/40 bg-slate-900/50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Tally outstanding</div>
          <div className="font-mono tabular-nums text-2xl font-bold text-cyan-100 mt-1">{fmtINR(total)}</div>
        </div>
        <div className="rounded-md border border-cyan-900/40 bg-slate-900/50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Current outstanding</div>
          <div className="font-mono tabular-nums text-2xl font-bold text-cyan-100 mt-1">{fmtINR(totalCurrent)}</div>
        </div>
        <div className="rounded-md border border-amber-900/40 bg-slate-900/50 p-3" data-testid="outstanding-planned-payment-tile">
          <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">Planned payments</div>
          <div className="font-mono tabular-nums text-2xl font-bold text-amber-200 mt-1">{fmtINR(totalPlanned)}</div>
          <div className="text-[10px] text-amber-400/70 mt-0.5">→ Committed Outflow</div>
        </div>
        <div className="rounded-md border border-emerald-900/40 bg-slate-900/50 p-3" data-testid="outstanding-fresh-tile">
          <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">Fresh outstanding</div>
          <div className="font-mono tabular-nums text-2xl font-bold text-emerald-200 mt-1">{fmtINR(totalFresh)}</div>
          <div className="text-[10px] text-emerald-400/70 mt-0.5">after approved payments</div>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-md border border-cyan-900/40 bg-slate-900/30 p-3 flex flex-wrap items-center gap-3" data-testid="outstanding-filters">
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Vendor</label>
          <input
            type="text"
            value={vendorQuery}
            onChange={(e) => setVendorQuery(e.target.value)}
            placeholder="Search by name…"
            className="h-8 w-56 px-2.5 text-xs rounded-sm bg-slate-950 border border-cyan-900/50 text-cyan-100 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-cyan-400"
            data-testid="filter-vendor-input"
          />
        </div>

        <FilterPills
          label="Payment Saved"
          testid="filter-saved"
          value={savedFilter}
          onChange={setSavedFilter}
          options={[
            { value: "all",       label: "All" },
            { value: "saved",     label: "Saved" },
            { value: "not_saved", label: "Not saved" },
          ]}
        />

        <FilterPills
          label="Payment Approved"
          testid="filter-approved"
          value={approvedFilter}
          onChange={setApprovedFilter}
          options={[
            { value: "all",      label: "All" },
            { value: "approved", label: "Approved" },
            { value: "pending",  label: "Pending" },
          ]}
        />

        <div className="ml-auto flex items-center gap-3">
          <div className="text-[11px] text-slate-400 font-mono tabular-nums" data-testid="filter-count">
            Showing <span className="text-cyan-200 font-bold">{filteredItems.length}</span> of <span className="text-cyan-200 font-bold">{items.length}</span>
          </div>
          {filtersActive && (
            <button
              onClick={clearFilters}
              className="text-[11px] font-bold uppercase tracking-wider text-slate-300 hover:text-cyan-200 px-2 py-1 rounded-sm border border-slate-700"
              data-testid="filter-clear-btn"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Top horizontal scrollbar that mirrors the body */}
      <div className="rounded-md border border-cyan-900/40 bg-slate-900/40 overflow-hidden">
        <div ref={topRef} className="overflow-x-auto" data-testid="outstanding-top-scrollbar" style={{ height: 14 }}>
          <div style={{ width: scrollWidth || 1, height: 1 }} />
        </div>

        {/* Body: sticky header + sticky first/last columns */}
        <div ref={bodyRef} className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="text-sm border-collapse" data-testid="outstanding-table" style={{ minWidth: "100%" }}>
            <thead className="sticky top-0 z-20">
              <tr className="bg-slate-900 text-[10px] uppercase tracking-wider text-cyan-200 font-bold">
                <SortableTh sortKey="vendor" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}
                  className="text-left px-4 py-3 sticky left-0 z-30 bg-slate-900 border-r border-cyan-900/40 min-w-[220px]">
                  Vendor
                </SortableTh>
                <SortableTh sortKey="product" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}
                  className="text-left px-4 py-3 bg-slate-900 min-w-[140px]">
                  Product
                </SortableTh>
                <SortableTh sortKey="total_outstanding" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} align="right"
                  className="text-right px-4 py-3 bg-slate-900 min-w-[130px]">
                  Total Outstanding
                </SortableTh>
                <SortableTh sortKey="last_purchase_date" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}
                  className="text-left px-4 py-3 bg-slate-900 min-w-[110px]">
                  Last Purchase Date
                </SortableTh>
                <SortableTh sortKey="last_purchase_amount" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} align="right"
                  className="text-right px-4 py-3 bg-slate-900 min-w-[130px]">
                  Last Purchase Amount
                </SortableTh>
                {Array.from({ length: poCount }).map((_, i) => (
                  <React.Fragment key={`hdr-po-${i}`}>
                    <th className="text-left px-2 py-3 bg-slate-900 border-l border-cyan-900/30 min-w-[110px]">PO #{i + 1}</th>
                    <th className="text-left px-2 py-3 bg-slate-900 min-w-[100px]">Date</th>
                    <th className="text-right px-2 py-3 bg-slate-900 min-w-[100px]">Amount</th>
                  </React.Fragment>
                ))}
                <SortableTh sortKey="planned_payment" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}
                  className="text-left px-3 py-3 bg-slate-900 border-l-2 border-amber-700/50 min-w-[200px]"
                  testid="header-payment">
                  <span className="text-amber-300">Payment</span>
                  <div className="text-[9px] text-slate-500 normal-case font-normal tracking-normal mt-0.5">amount · approve · save</div>
                </SortableTh>
                <SortableTh sortKey="current_outstanding" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} align="right"
                  className="text-right px-4 py-3 sticky right-0 z-30 text-cyan-50 border-l-2 border-cyan-500/60 min-w-[150px]"
                  style={{ backgroundColor: "#0e2433" }}>
                  Current Outstanding
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={STATIC_LEFT_COUNT + poCount * 3 + 2} className="px-4 py-8 text-center text-slate-400 text-xs">Loading…</td></tr>
              )}
              {!loading && filteredItems.length === 0 && (
                <tr>
                  <td colSpan={STATIC_LEFT_COUNT + poCount * 3 + 2} className="px-4 py-10 text-center">
                    <FileXls size={32} weight="duotone" className="text-cyan-700 inline-block mb-2" />
                    <div className="text-sm text-slate-300 font-semibold">
                      {items.length === 0 ? "No vendor ledgers yet" : "No vendors match the current filters"}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {items.length === 0 ? (
                        <>Click <span className="font-bold text-cyan-300">Upload ledger(s)</span> and drop one XLSX per vendor.</>
                      ) : (
                        <>Try clearing filters above.</>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              {!loading && filteredItems.map((row) => {
                const pos = row.pos_after_last_purchase || [];
                return (
                  <tr key={row.id} className="border-t border-cyan-900/30 hover:bg-slate-900/40" data-testid={`outstanding-row-${row.id}`}>
                    <td className="px-4 py-3 sticky left-0 z-10 border-r border-cyan-900/40" style={{ backgroundColor: "#0a1a2b" }}>
                      <div className="flex items-center gap-2 group/v">
                        <Storefront size={13} weight="duotone" className="text-cyan-300 shrink-0" />
                        <VendorHoverWithPin row={row} onDownload={() => downloadVendorLedger(row)} />
                        <button
                          onClick={() => remove(row.id, row.vendor)}
                          className="opacity-0 group-hover/v:opacity-100 text-slate-500 hover:text-rose-400 p-1 rounded-sm shrink-0 transition-opacity"
                          title="Remove"
                          data-testid={`outstanding-delete-${row.id}`}
                        >
                          <Trash size={13} weight="bold" />
                        </button>
                      </div>
                      {row.source_file && (
                        <div className="text-[10px] text-slate-500 mt-0.5 ml-5 font-mono truncate max-w-[200px]" title={row.source_file}>
                          {row.source_file}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-200 whitespace-nowrap">{row.product || <span className="text-slate-500">—</span>}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums font-bold text-cyan-100">{fmtINR(row.total_outstanding)}</td>
                    <td className="px-4 py-3 text-slate-300 font-mono tabular-nums whitespace-nowrap">
                      {row.last_purchase_date ? fmtDate(row.last_purchase_date) : <span className="text-slate-500">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-slate-200">
                      {row.last_purchase_amount != null ? fmtINR(row.last_purchase_amount) : <span className="text-slate-500">—</span>}
                    </td>
                    {Array.from({ length: poCount }).map((_, i) => {
                      const po = pos[i];
                      return (
                        <React.Fragment key={`${row.id}-po-${i}`}>
                          <td className="px-2 py-3 border-l border-cyan-900/20 font-mono tabular-nums text-[11px] text-slate-300 whitespace-nowrap" title={po?.outlet || ""}>
                            {po ? po.po_number : <span className="text-slate-700">—</span>}
                          </td>
                          <td className="px-2 py-3 font-mono tabular-nums text-[11px] text-slate-400 whitespace-nowrap">
                            {po?.po_date ? fmtDate(po.po_date) : <span className="text-slate-700">—</span>}
                          </td>
                          <td className="px-2 py-3 text-right font-mono tabular-nums text-[11px] text-cyan-200">
                            {po ? fmtINR(po.amount) : <span className="text-slate-700">—</span>}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    {/* Payment cell */}
                    <td className="px-3 py-2 border-l-2 border-amber-700/40 align-top" data-testid={`payment-cell-${row.id}`}>
                      {isEditing(row.id) ? (
                        <div className="flex flex-col gap-1.5">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={edit[row.id].amount}
                            onChange={(e) => setEditField(row.id, "amount", e.target.value)}
                            placeholder="Amount"
                            className="h-7 w-32 px-2 text-xs font-mono tabular-nums rounded-sm bg-slate-950 border border-amber-700/50 text-amber-100 focus:outline-none focus:ring-1 focus:ring-amber-400 placeholder:text-slate-600"
                            data-testid={`payment-input-${row.id}`}
                            autoFocus
                          />
                          <label className="flex items-center gap-1.5 text-[10px] text-slate-300 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={edit[row.id].approved}
                              onChange={(e) => setEditField(row.id, "approved", e.target.checked)}
                              className="h-3 w-3 accent-amber-500"
                              data-testid={`payment-approve-${row.id}`}
                            />
                            Payment to be made
                          </label>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => savePayment(row)}
                              className="h-6 px-2 text-[10px] font-bold rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white inline-flex items-center gap-1"
                              data-testid={`payment-save-${row.id}`}
                            >
                              <FloppyDisk size={11} weight="bold" /> Save
                            </button>
                            <button
                              onClick={() => cancelEdit(row.id)}
                              className="h-6 px-2 text-[10px] font-bold rounded-sm text-slate-400 hover:bg-slate-800"
                              data-testid={`payment-cancel-${row.id}`}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-mono tabular-nums text-sm font-bold text-amber-200">
                              {row.planned_payment > 0 ? fmtINR(row.planned_payment) : <span className="text-slate-600">—</span>}
                            </div>
                            <div className="text-[10px] mt-0.5 flex items-center gap-1">
                              {row.payment_approved && row.planned_payment > 0 ? (
                                <span className="inline-flex items-center gap-1 text-emerald-400 font-semibold">
                                  <CheckCircle size={10} weight="fill" /> Approved
                                </span>
                              ) : row.planned_payment > 0 ? (
                                <span className="text-slate-500">Pending approval</span>
                              ) : (
                                <span className="text-slate-600">No payment set</span>
                              )}
                            </div>
                            {row.last_paid_amount > 0 && row.last_paid_at && (
                              <div className="text-[9px] text-slate-500 mt-0.5 font-mono">
                                Last paid {fmtINR(row.last_paid_amount)} · {fmtDate(row.last_paid_at)}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => startEdit(row)}
                            className="text-slate-500 hover:text-amber-300 p-1 rounded-sm shrink-0"
                            title="Edit payment"
                            data-testid={`payment-edit-${row.id}`}
                          >
                            <PencilSimple size={12} weight="bold" />
                          </button>
                          {(row.planned_payment > 0 || row.payment_approved) && (
                            <button
                              onClick={() => rollbackRowPayment(row)}
                              className="text-slate-500 hover:text-rose-400 p-1 rounded-sm shrink-0"
                              title={row.payment_approved
                                ? `Rollback approved payment of ${fmtINR(row.planned_payment)}`
                                : `Rollback planned payment of ${fmtINR(row.planned_payment)}`}
                              data-testid={`payment-rollback-${row.id}`}
                            >
                              <ArrowUUpLeft size={12} weight="bold" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums font-bold sticky right-0 z-10 text-cyan-50 border-l-2 border-cyan-500/60" style={{ backgroundColor: "#0e2433" }}>
                      <div>{fmtINR(row.current_outstanding)}</div>
                      {row.fresh_outstanding !== row.current_outstanding && (
                        <div className="text-[10px] text-emerald-300 font-normal mt-0.5">
                          Fresh: {fmtINR(row.fresh_outstanding)}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {!loading && filteredItems.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-cyan-700/60 text-cyan-100 font-bold">
                  <td className="px-4 py-3 sticky left-0 z-10 bg-slate-900 text-[11px] uppercase tracking-wider border-r border-cyan-900/40">
                    {filtersActive ? "Filtered total" : "Total"}
                  </td>
                  <td className="px-4 py-3 bg-slate-900" />
                  <td className="px-4 py-3 bg-slate-900 text-right font-mono tabular-nums">{fmtINR(fTotal)}</td>
                  <td className="px-4 py-3 bg-slate-900" />
                  <td className="px-4 py-3 bg-slate-900" />
                  {Array.from({ length: poCount }).map((_, i) => (
                    <React.Fragment key={`ft-${i}`}>
                      <td className="px-2 py-3 bg-slate-900" />
                      <td className="px-2 py-3 bg-slate-900" />
                      <td className="px-2 py-3 bg-slate-900" />
                    </React.Fragment>
                  ))}
                  <td className="px-3 py-3 bg-slate-900 border-l-2 border-amber-700/40 text-amber-200 font-mono tabular-nums">{fmtINR(fTotalPlanned)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums sticky right-0 z-10 text-cyan-50 border-l-2 border-cyan-500/60" style={{ backgroundColor: "#0e2433" }}>
                    <div>{fmtINR(fTotalCurrent)}</div>
                    {fTotalFresh !== fTotalCurrent && (
                      <div className="text-[10px] text-emerald-300 font-normal mt-0.5">Fresh: {fmtINR(fTotalFresh)}</div>
                    )}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Report preview modal */}
      {reportOpen && (
        <ReportPreviewModal
          loading={reportLoading}
          data={reportData}
          onClose={() => setReportOpen(false)}
          onDownload={downloadAllVendorsReport}
        />
      )}

      {/* Confirmation: planned payment exceeds current outstanding */}
      <AlertDialog open={!!confirmRow} onOpenChange={(open) => { if (!open) setConfirmRow(null); }}>
        <AlertDialogContent data-testid="payment-overdraw-dialog" className="bg-slate-900 border border-amber-700/60 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-300 text-base">
              <Warning size={20} weight="fill" />
              Payment is greater than Current Outstanding
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-slate-300 space-y-2 mt-2">
                {confirmRow && (
                  <div className="rounded-sm border border-amber-700/30 bg-slate-950/60 p-3 font-mono tabular-nums text-xs space-y-1">
                    <div className="flex justify-between"><span className="text-slate-400">Vendor</span><span className="text-cyan-100 font-semibold">{confirmRow.row.vendor}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Current Outstanding</span><span className="text-cyan-100">{fmtINR(confirmRow.row.current_outstanding)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Planned Payment</span><span className="text-amber-200">{fmtINR(confirmRow.planned)}</span></div>
                    <div className="flex justify-between border-t border-amber-700/30 pt-1 mt-1">
                      <span className="text-slate-400">Over by</span>
                      <span className="text-rose-300 font-bold">{fmtINR(confirmRow.planned - (Number(confirmRow.row.current_outstanding) || 0))}</span>
                    </div>
                  </div>
                )}
                <div className="text-slate-300">Still you want to <span className="font-bold text-amber-300">PROCEED</span>?</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              data-testid="payment-overdraw-no"
              className="bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700"
            >
              NO
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="payment-overdraw-yes"
              onClick={proceedWithOverdrawnPayment}
              className="bg-amber-600 hover:bg-amber-500 text-white"
            >
              YES
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FilterPills({ label, value, onChange, options, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</span>
      <div className="inline-flex rounded-sm border border-cyan-900/50 bg-slate-950 overflow-hidden">
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              data-testid={`${testid}-${opt.value}`}
              className={
                "px-2.5 h-8 text-[11px] font-semibold transition-colors " +
                (active
                  ? "bg-cyan-600 text-white"
                  : "text-slate-300 hover:bg-slate-800")
              }
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SortableTh({ sortKey, sortBy, sortDir, onClick, className, align = "left", style, testid, children }) {
  const active = sortBy === sortKey;
  const justify = align === "right" ? "justify-end" : "justify-start";
  return (
    <th
      className={(className || "") + " cursor-pointer select-none hover:bg-slate-800"}
      style={style}
      onClick={() => onClick(sortKey)}
      data-testid={testid || `sort-${sortKey}`}
      title={`Sort by ${sortKey.replace(/_/g, " ")} (click to toggle)`}
    >
      <span className={`inline-flex items-center gap-1 align-middle ${justify} w-full`}>
        <span>{children}</span>
        <span className={`shrink-0 ${active ? "text-cyan-300" : "text-slate-600"}`}>
          {active && sortDir === "asc"  ? <CaretUp size={10} weight="bold" /> :
           active && sortDir === "desc" ? <CaretDown size={10} weight="bold" /> :
           <CaretDown size={10} weight="regular" className="opacity-30" />}
        </span>
      </span>
    </th>
  );
}

function VendorHoverWithPin({ row, onDownload }) {
  const [hoverOpen, setHoverOpen] = useState(false);
  const [pinned, setPinned]       = useState(false);
  const open = pinned || hoverOpen;

  // Close pinned popover on Escape
  useEffect(() => {
    if (!pinned) return;
    const onKey = (e) => { if (e.key === "Escape") setPinned(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pinned]);

  return (
    <>
      {/* When pinned, dim the rest of the screen so the popover is the obvious focus. */}
      {pinned && (
        <div
          className="fixed inset-0 z-[9990] bg-black/60 backdrop-blur-[2px]"
          onClick={() => setPinned(false)}
          data-testid="hover-pinned-backdrop"
        />
      )}
      <HoverCard
      openDelay={120}
      closeDelay={150}
      open={open}
      onOpenChange={(v) => {
        // When the user is hovering, allow Radix to drive open state.
        // When pinned, we own the state — ignore hover-close attempts.
        if (pinned) return;
        setHoverOpen(v);
      }}
    >
      <HoverCardTrigger asChild>
        <button
          className="font-semibold text-cyan-50 flex-1 min-w-0 text-left underline decoration-cyan-700/50 decoration-dashed underline-offset-4 hover:decoration-cyan-300 hover:text-cyan-100 cursor-pointer"
          data-testid={`outstanding-vendor-name-${row.id}`}
          type="button"
          onClick={(e) => {
            // Clicking the vendor name pins it. Click again to unpin.
            e.preventDefault();
            setPinned((p) => !p);
          }}
        >
          {row.vendor}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="bottom"
        align="start"
        sideOffset={8}
        avoidCollisions
        // z-[9999] guarantees we're above EVERY app element.
        // The `vendor-hover-content` class disables the global
        // `[data-radix-popper-content-wrapper] > *` gradient rule in index.css
        // that was making the popover translucent.
        className="vendor-hover-content w-[720px] max-w-[92vw] z-[9999] border-2 border-cyan-400/70 text-slate-100 p-0 overflow-hidden rounded-md"
        data-testid={`outstanding-hover-card-${row.id}`}
        // When pinned, prevent Radix from auto-closing on outside-pointer events
        onPointerDownOutside={(e) => { if (pinned) e.preventDefault(); }}
        onInteractOutside={(e) => { if (pinned) e.preventDefault(); }}
        onFocusOutside={(e) => { if (pinned) e.preventDefault(); }}
      >
        <VendorLedgerHoverPreview
          row={row}
          onDownload={onDownload}
          pinned={pinned}
          onTogglePin={() => setPinned((p) => !p)}
          onClose={() => { setPinned(false); setHoverOpen(false); }}
        />
      </HoverCardContent>
    </HoverCard>
    </>
  );
}


function VendorLedgerHoverPreview({ row, onDownload, pinned, onTogglePin, onClose }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]       = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    api.get(`/outstanding/${row.id}/preview`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Failed to load preview"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [row.id]);

  return (
    <div className="text-xs" style={{ backgroundColor: "#1e293b" }} data-testid="vendor-hover-preview">
      {/* Header */}
      <div className="px-3 py-2.5 bg-gradient-to-r from-cyan-900/40 to-slate-900 border-b border-cyan-900/60">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex items-start gap-1.5">
            {pinned && (
              <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-amber-900/40 border border-amber-700/50 text-amber-200 text-[9px] font-bold uppercase tracking-wider mt-0.5">
                <PushPin size={9} weight="fill" /> Pinned
              </span>
            )}
            <div className="min-w-0">
              <div className="font-bold text-cyan-100 truncate">{row.vendor}</div>
              <div className="text-[10px] text-slate-400 truncate" title={row.product || ""}>
                {row.product || "—"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={onTogglePin}
              className={
                "h-7 w-7 grid place-items-center rounded-sm cursor-pointer transition-colors " +
                (pinned
                  ? "bg-amber-600 hover:bg-amber-500 text-white"
                  : "bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-amber-200")
              }
              data-testid="hover-pin-btn"
              title={pinned ? "Unpin (let it close on mouse-leave)" : "Pin (keep open after mouse-leave)"}
            >
              {pinned ? <PushPinSlash size={12} weight="bold" /> : <PushPin size={12} weight="bold" />}
            </button>
            <button
              onClick={onDownload}
              className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-sm bg-cyan-600 hover:bg-cyan-500 text-white text-[11px] font-bold uppercase tracking-wider cursor-pointer"
              data-testid="hover-download-ledger-btn"
              title="Download full ledger (XLSX)"
            >
              <DownloadSimple size={12} weight="bold" />
              Download
            </button>
            {pinned && (
              <button
                onClick={onClose}
                className="h-7 w-7 grid place-items-center rounded-sm bg-slate-800 hover:bg-rose-900/40 text-slate-400 hover:text-rose-300 cursor-pointer"
                data-testid="hover-close-btn"
                title="Close (ESC)"
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary strip */}
      <div className="px-3 py-2 grid grid-cols-4 gap-2 border-b border-cyan-900/40">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Current</div>
          <div className="font-mono tabular-nums text-cyan-100 font-bold text-[13px] mt-0.5">{fmtINR(row.current_outstanding)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Tally</div>
          <div className="font-mono tabular-nums text-slate-200 text-[13px] mt-0.5">{fmtINR(row.total_outstanding)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-rose-300 font-bold">Debit</div>
          <div className="font-mono tabular-nums text-rose-200 text-[13px] mt-0.5">{fmtINR(data?.total_debit ?? 0)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-emerald-300 font-bold">Credit</div>
          <div className="font-mono tabular-nums text-emerald-200 text-[13px] mt-0.5">{fmtINR(data?.total_credit ?? 0)}</div>
        </div>
      </div>

      {/* Preview rows — vertical & horizontal scroll */}
      <div className="max-h-[280px] overflow-auto" style={{ backgroundColor: "#1e293b" }}>
        {loading && <div className="p-3 text-center text-slate-500 text-[11px]">Loading ledger…</div>}
        {err && <div className="p-3 text-center text-rose-400 text-[11px]">{err}</div>}
        {data && (
          <div>
            {(data.recent_pos || []).length > 0 && (
              <div className="border-b border-cyan-900/30">
                <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-wider font-bold text-amber-300 sticky top-0 bg-slate-950 z-10">
                  Recent POs (after last Tally purchase)
                </div>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-[9px] uppercase tracking-wider text-amber-300/70 border-b border-amber-900/40">
                      <th className="text-left px-3 py-1 font-bold">Date</th>
                      <th className="text-left px-2 py-1 font-bold">PO #</th>
                      <th className="text-left px-2 py-1 font-bold">Outlet</th>
                      <th className="text-right px-3 py-1 font-bold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_pos.map((p, i) => (
                      <tr key={`po-${i}`} className="border-t border-amber-900/20">
                        <td className="px-3 py-1 font-mono text-slate-400 whitespace-nowrap">{fmtDate(p.po_date)}</td>
                        <td className="px-2 py-1 font-mono text-amber-200">{p.po_number}</td>
                        <td className="px-2 py-1 text-slate-300 truncate max-w-[160px]" title={p.outlet || ""}>{p.outlet || "—"}</td>
                        <td className="px-3 py-1 text-right font-mono tabular-nums text-amber-100 font-bold whitespace-nowrap">{fmtINR(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(data.recent_transactions || []).length > 0 ? (
              <div>
                <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-wider font-bold text-cyan-300 flex items-center gap-1 sticky top-0 bg-slate-950 z-10">
                  <Calendar size={10} weight="bold" /> Latest Tally transactions
                </div>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-[9px] uppercase tracking-wider text-cyan-300/70 border-b border-cyan-900/40">
                      <th className="text-left px-3 py-1 font-bold whitespace-nowrap">Date</th>
                      <th className="text-left px-2 py-1 font-bold">Particulars</th>
                      <th className="text-left px-2 py-1 font-bold whitespace-nowrap">Vch</th>
                      <th className="text-right px-2 py-1 font-bold text-rose-300/80 whitespace-nowrap">Debit</th>
                      <th className="text-right px-3 py-1 font-bold text-emerald-300/80 whitespace-nowrap">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_transactions.map((t, i) => (
                      <tr key={`tx-${i}`} className="border-t border-cyan-900/20 hover:bg-slate-900/60">
                        <td className="px-3 py-1 font-mono text-slate-400 whitespace-nowrap">{fmtDate(t.date)}</td>
                        <td className="px-2 py-1 text-slate-200 truncate max-w-[200px]" title={t.particulars}>{t.particulars || "—"}</td>
                        <td className="px-2 py-1 text-slate-400 whitespace-nowrap text-[10px]">
                          {t.vch_type}{t.vch_no ? <span className="text-slate-600"> · {t.vch_no}</span> : null}
                        </td>
                        <td className="px-2 py-1 text-right font-mono tabular-nums text-rose-200 whitespace-nowrap">{t.debit > 0 ? fmtINR(t.debit) : "—"}</td>
                        <td className="px-3 py-1 text-right font-mono tabular-nums text-emerald-200 whitespace-nowrap">{t.credit > 0 ? fmtINR(t.credit) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              !loading && (data.recent_pos || []).length === 0 && (
                <div className="p-3 text-center text-amber-300/80 text-[11px] bg-amber-950/20 m-3 rounded-sm border border-amber-900/40">
                  No transaction detail stored. <strong>Re-upload the Tally XLS file</strong> to populate Date / Debit / Credit rows.
                </div>
              )
            )}
            {/* If new schema unavailable but old upload, hint subtly */}
            {!data.has_transactions && (data.recent_transactions || []).length === 0 && (data.recent_pos || []).length > 0 && (
              <div className="p-3 text-center text-amber-300/70 text-[10px] bg-amber-950/10 border-t border-amber-900/30">
                Tally transaction detail not yet captured. Re-upload the vendor XLS to see Debit/Credit rows.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-3 py-1.5 bg-slate-900/80 border-t border-cyan-900/60 text-[10px] text-slate-500 flex items-center justify-between gap-2">
        <span>
          {data && data.has_transactions
            ? <>Latest {data.recent_transactions.length} of {data.purchase_count}+ transactions · scroll for more</>
            : <>Re-upload XLS for full detail.</>}
        </span>
        <span className="shrink-0 text-slate-600">
          {pinned ? <>ESC or × to close</> : <>Click vendor name to pin</>}
        </span>
      </div>
    </div>
  );
}

function ReportPreviewModal({ loading, data, onClose, onDownload }) {
  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="report-preview-modal"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Dialog */}
      <div className="relative w-full max-w-[95vw] max-h-[92vh] flex flex-col bg-slate-900 border border-cyan-900/60 rounded-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-cyan-900/60 bg-slate-950">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-sm grid place-items-center bg-cyan-900/40 text-cyan-200">
              <FileXls size={16} weight="duotone" />
            </div>
            <div>
              <div className="text-base font-bold text-cyan-100">Vendors Pending List</div>
              <div className="text-[11px] text-slate-400">
                {data ? <>Generated <span className="text-cyan-200 font-mono">{data.generated_at}</span> · {data.vendors.length} vendors · last {data.month_labels.length} months</> : "Loading…"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={onDownload}
              disabled={loading || !data}
              className="h-9 rounded-sm bg-cyan-600 hover:bg-cyan-500 text-white disabled:opacity-40"
              data-testid="report-download-xlsx-btn"
            >
              <DownloadSimple size={14} weight="bold" className="mr-1.5" />
              Download XLSX
            </Button>
            <button
              onClick={onClose}
              className="h-9 px-3 rounded-sm text-slate-300 hover:text-cyan-200 hover:bg-slate-800 text-xs font-bold uppercase tracking-wider"
              data-testid="report-close-btn"
            >
              Close
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="p-10 text-center text-slate-400 text-sm">Building report…</div>
          )}
          {data && data.stale_ledgers > 0 && (
            <div
              className="px-5 py-2.5 border-b border-amber-900/60 bg-amber-950/40 text-[11px] text-amber-200 flex items-center gap-2"
              data-testid="report-stale-banner"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" />
              <span>
                <strong className="font-bold text-amber-100">{data.stale_ledgers} of {data.total_ledgers}</strong> vendor ledgers
                were parsed with the older aggregate parser. Re-upload the Tally Outstanding Excel
                to switch these vendors to bill-wise (New Ref / Agst Ref) ageing for exact reconciliation.
              </span>
            </div>
          )}
          {data && (
            <table className="text-xs border-collapse w-full" data-testid="report-preview-table">
              <thead className="sticky top-0 z-20">
                <tr className="bg-slate-950 text-[10px] uppercase tracking-wider text-cyan-200 font-bold">
                  <th className="text-left px-3 py-2.5 sticky left-0 z-30 bg-slate-950 border-r border-cyan-900/40 min-w-[260px]">Particulars</th>
                  <th className="text-right px-3 py-2.5 bg-slate-950 min-w-[120px] border-r border-cyan-900/40">Balance</th>
                  <th className="text-right px-3 py-2.5 bg-slate-950 min-w-[110px]">Bills</th>
                  <th className="text-right px-3 py-2.5 bg-slate-950 min-w-[110px] border-r border-cyan-900/40">Payment</th>
                  {data.month_labels.map((lbl) => (
                    <th key={lbl} className="text-right px-2.5 py-2.5 bg-slate-950 min-w-[88px]">{lbl}</th>
                  ))}
                  <th className="text-right px-3 py-2.5 bg-slate-950 min-w-[100px] border-l border-cyan-900/40">Last year</th>
                </tr>
              </thead>
              <tbody>
                {data.vendors.map((v, idx) => (
                  <tr key={idx} className="border-t border-cyan-900/20 hover:bg-slate-900/60">
                    <td className="px-3 py-2 sticky left-0 z-10 bg-slate-900/80 border-r border-cyan-900/30 text-cyan-50 font-semibold">
                      <div className="flex items-center gap-1.5">
                        <span>{v.vendor}</span>
                        {v.reconciliation_ok === false && (
                          <span
                            title={`Reconciliation diff: ₹${(v.reconciliation_diff || 0).toLocaleString('en-IN')}`}
                            className="inline-flex items-center h-4 px-1.5 rounded-sm bg-amber-950/60 text-amber-300 text-[9px] font-bold uppercase tracking-wider border border-amber-800/60"
                            data-testid={`recon-warn-${idx}`}
                          >
                            ⚠ recon
                          </span>
                        )}
                        {v.bill_wise === false && (
                          <span
                            title="This vendor's data was parsed with the old aggregate parser. Re-upload for bill-wise ageing."
                            className="inline-flex items-center h-4 px-1.5 rounded-sm bg-slate-800 text-slate-400 text-[9px] font-bold uppercase tracking-wider border border-slate-700"
                            data-testid={`legacy-badge-${idx}`}
                          >
                            legacy
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={`px-3 py-2 text-right font-mono tabular-nums font-bold border-r border-cyan-900/30 ${(v.balance ?? v.closing_balance) < 0 ? "text-amber-300" : "text-cyan-100"}`}>{fmtINR(v.balance ?? v.closing_balance)}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-300">{fmtINR(v.bills ?? v.credit)}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-300 border-r border-cyan-900/30">{fmtINR(v.payment ?? v.debit)}</td>
                    {data.month_keys.map((k) => {
                      const val = v.months[k] || 0;
                      return (
                        <td key={k} className={`px-2.5 py-2 text-right font-mono tabular-nums ${val > 0 ? "text-amber-200" : "text-slate-700"}`}>
                          {val > 0 ? fmtINR(val) : "—"}
                        </td>
                      );
                    })}
                    <td className={`px-3 py-2 text-right font-mono tabular-nums border-l border-cyan-900/30 ${v.last_year > 0 ? "text-amber-200" : "text-slate-700"}`}>
                      {v.last_year > 0 ? fmtINR(v.last_year) : "—"}
                    </td>
                  </tr>
                ))}
                {data.vendors.length > 0 && (
                  <tr className="border-t-2 border-cyan-700/60 text-cyan-100 font-bold bg-slate-950">
                    <td className="px-3 py-3 sticky left-0 z-10 bg-slate-950 uppercase tracking-wider text-[11px] border-r border-cyan-900/40">Grand Total</td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums border-r border-cyan-900/40">{fmtINR(data.totals.balance ?? data.totals.closing_balance)}</td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums">{fmtINR(data.totals.bills ?? data.totals.credit)}</td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums border-r border-cyan-900/40">{fmtINR(data.totals.payment ?? data.totals.debit)}</td>
                    {data.month_keys.map((k) => {
                      const val = data.totals.months[k] || 0;
                      return (
                        <td key={k} className={`px-2.5 py-3 text-right font-mono tabular-nums ${val > 0 ? "text-amber-200" : "text-slate-700"}`}>
                          {val > 0 ? fmtINR(val) : "—"}
                        </td>
                      );
                    })}
                    <td className={`px-3 py-3 text-right font-mono tabular-nums border-l border-cyan-900/40 ${data.totals.last_year > 0 ? "text-amber-200" : "text-slate-700"}`}>
                      {data.totals.last_year > 0 ? fmtINR(data.totals.last_year) : "—"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {/* Statutory & Directors block — bank debits last 4 months */}
          {data && data.statutory_summary && data.statutory_summary.buckets && (
            <div className="mt-8 mx-3 mb-3 rounded-md border border-cyan-900/60 overflow-hidden" data-testid="statutory-block">
              <div className="px-4 py-2.5 bg-slate-950 border-b border-cyan-900/40 flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-cyan-200 font-bold">
                  Statutory & Directors · Bank Debits · Last 4 Months
                </div>
                <div className="text-[10px] text-slate-500 italic">
                  Source: outstanding ledger parties matched against bank statement debits
                </div>
              </div>
              <table className="text-xs border-collapse w-full" data-testid="statutory-table">
                <thead>
                  <tr className="bg-slate-900 text-[10px] uppercase tracking-wider text-cyan-200 font-bold">
                    <th className="text-left px-3 py-2 min-w-[220px]">Particulars</th>
                    {data.statutory_summary.month_labels.map((lbl) => (
                      <th key={lbl} className="text-right px-3 py-2 min-w-[110px]">{lbl}</th>
                    ))}
                    <th className="text-right px-3 py-2 min-w-[120px] border-l border-cyan-900/40 bg-slate-950">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.statutory_summary.buckets.map((b) => (
                    <tr key={b.id} className="border-t border-cyan-900/20 hover:bg-slate-900/60" data-testid={`statutory-row-${b.id}`}>
                      <td className="px-3 py-2 text-cyan-50 font-semibold">{b.label}</td>
                      {data.statutory_summary.month_keys.map((k) => {
                        const val = b.months[k] || 0;
                        return (
                          <td key={k} className={`px-3 py-2 text-right font-mono tabular-nums ${val > 0 ? "text-amber-200" : "text-slate-700"}`}>
                            {val > 0 ? fmtINR(val) : "—"}
                          </td>
                        );
                      })}
                      <td className={`px-3 py-2 text-right font-mono tabular-nums font-bold border-l border-cyan-900/40 ${b.total > 0 ? "text-cyan-100" : "text-slate-700"}`}>
                        {b.total > 0 ? fmtINR(b.total) : "—"}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-cyan-700/60 text-cyan-100 font-bold bg-slate-950" data-testid="statutory-row-total">
                    <td className="px-3 py-2.5 uppercase tracking-wider text-[11px]">Total</td>
                    {data.statutory_summary.month_keys.map((k) => {
                      const val = data.statutory_summary.totals.months[k] || 0;
                      return (
                        <td key={k} className={`px-3 py-2.5 text-right font-mono tabular-nums ${val > 0 ? "text-amber-200" : "text-slate-700"}`}>
                          {val > 0 ? fmtINR(val) : "—"}
                        </td>
                      );
                    })}
                    <td className={`px-3 py-2.5 text-right font-mono tabular-nums border-l border-cyan-900/40 ${data.statutory_summary.totals.total > 0 ? "text-cyan-100" : "text-slate-700"}`}>
                      {data.statutory_summary.totals.total > 0 ? fmtINR(data.statutory_summary.totals.total) : "—"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-cyan-900/60 bg-slate-950 px-5 py-2 text-[11px] text-slate-500">
          Closing Balance = Current Outstanding (Tally + recent POs). Monthly columns include both Tally purchases and PetPooja POs bucketed by date.
        </div>
      </div>
    </div>
  );
}
